/**
 * =============================================================================
 * FALCON - Module Deneigement des Parkings Avion
 * =============================================================================
 *
 * @file        App.tsx
 * @description Composant racine de l'application React
 *              Configure le routage et la protection des routes authentifiees
 *
 * @routes      PUBLIQUES
 *              /login                          - Page de connexion
 *
 *              PROTEGEES (authentification requise)
 *              /                               - Dashboard principal
 *              /deneignements                  - Liste des interventions
 *              /deneignements/nouveau          - Creation d'intervention
 *              /deneignements/:id              - Detail d'une intervention
 *              /deneignements/:id/modifier     - Modification d'intervention
 *              /deneignements/:id/zones        - Suivi terrain des zones
 *              /regulation                     - Ecran de regulation temps reel
 *              /agent                          - Page d'accueil agent mobile
 *
 *              ADMINISTRATION
 *              /administration/equipes         - Gestion des equipes
 *              /administration/parkings        - Gestion des parkings
 *              /administration/materiel        - Gestion du materiel
 *              /administration/notifications   - Configuration notifications
 *              /administration/utilisateurs    - Gestion des utilisateurs
 *              /administration/planning        - Planning des vacations
 *              /exports                        - Exports et rapports
 *
 * @version     2.1
 * @author      Equipe FALCON
 * =============================================================================
 */

import { Routes, Route, Navigate } from 'react-router-dom';

// =============================================================================
// IMPORTS DES PAGES
// =============================================================================

// Layout principal (sidebar + header + content)
import Layout from '@/components/common/Layout';

// Composant de protection des routes avec gestion des roles
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';

// Page publique
import LoginPage from '@/pages/LoginPage';

// Pages principales
import DashboardPage from '@/pages/DashboardPage';
import DeneignementListPage from '@/pages/DeneignementListPage';
import DeneignementDetailPage from '@/pages/DeneignementDetailPage';
import DeneignementFormPage from '@/pages/DeneignementFormPage';

// Pages Administration
import EquipesPage from '@/pages/admin/EquipesPage';
import ParkingsPage from '@/pages/admin/ParkingsPage';
import MaterielPage from '@/pages/admin/MaterielPage';
import CompagniesPage from '@/pages/admin/CompagniesPage';
import NotificationsPage from '@/pages/admin/NotificationsPage';
import UsersPage from '@/pages/admin/UsersPage';
import PlanningPage from '@/pages/admin/PlanningPage';
import ImportLogsPage from '@/pages/admin/ImportLogsPage';
import ExportsPage from '@/pages/ExportsPage';

// Pages Agent terrain (suivi des zones)
import ZonesAgentPage from '@/pages/agent/ZonesAgentPage';
import ZonesAgentPageOptimized from '@/pages/agent/ZonesAgentPageOptimized';
import MobileHomePage from '@/pages/agent/MobileHomePage';
import MobileProfilPage from '@/pages/agent/MobileProfilPage';

// Pages Regulation (ecran temps reel)
import RegulationPage from '@/pages/RegulationPage';
import RegulationTimelinePage from '@/pages/RegulationTimelinePage';

// Pages Utilisateur
import ProfilePage from '@/pages/ProfilePage';
import UnauthorizedPage from '@/pages/UnauthorizedPage';

/**
 * Constantes pour les roles
 */
const ROLES = {
  ADMIN: 'ROLE_ADMIN',
  GID_REGUL: 'ROLE_GID_REGUL',
  REGULATEUR: 'ROLE_REGULATEUR',
  CHEF_DENEIGNEMENT: 'ROLE_CHEF_DENEIGNEMENT',
  AGENT_DENEIGNEMENT: 'ROLE_AGENT_DENEIGNEMENT',
  COMPAGNIE: 'ROLE_COMPAGNIE',
};

/**
 * Application principale FALCON - Module Deneigement
 */
function App() {
  return (
    <Routes>
      {/* Routes publiques */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/unauthorized" element={<UnauthorizedPage />} />

      {/* Routes protegees avec Layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }
      >
        {/* Dashboard - accessible a tous les utilisateurs authentifies */}
        <Route index element={<DashboardPage />} />

        {/* Interventions - accessibles a tous les utilisateurs authentifies */}
        <Route path="deneignements" element={<DeneignementListPage />} />
        <Route path="deneignements/nouveau" element={<DeneignementFormPage />} />
        <Route path="deneignements/:id" element={<DeneignementDetailPage />} />
        <Route path="deneignements/:id/modifier" element={<DeneignementFormPage />} />
        <Route path="deneignements/:id/zones" element={<ZonesAgentPage />} />

        {/* Module Regulation - restreint aux regulateurs */}
        <Route
          path="regulation"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN, ROLES.REGULATEUR, ROLES.GID_REGUL]}>
              <RegulationPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="regulation/timeline"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN, ROLES.REGULATEUR, ROLES.GID_REGUL]}>
              <RegulationTimelinePage />
            </ProtectedRoute>
          }
        />

        {/* Exports - restreint aux regulateurs et admins */}
        <Route
          path="exports"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN, ROLES.GID_REGUL, ROLES.REGULATEUR]}>
              <ExportsPage />
            </ProtectedRoute>
          }
        />

        {/* Profil utilisateur - accessible a tous les utilisateurs authentifies */}
        <Route path="profile" element={<ProfilePage />} />

        {/* =================================================================== */}
        {/* SECTION ADMINISTRATION - ROLE_ADMIN requis                          */}
        {/* =================================================================== */}
        <Route
          path="administration/utilisateurs"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <UsersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/equipes"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <EquipesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/parkings"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <ParkingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/materiel"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <MaterielPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/compagnies"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <CompagniesPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/notifications"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <NotificationsPage />
            </ProtectedRoute>
          }
        />
        {/* Planning - accessible aux admins et chefs d'equipe */}
        <Route
          path="administration/planning"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN, ROLES.CHEF_DENEIGNEMENT]}>
              <PlanningPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="administration/imports"
          element={
            <ProtectedRoute roles={[ROLES.ADMIN]}>
              <ImportLogsPage />
            </ProtectedRoute>
          }
        />
      </Route>

      {/* =================================================================== */}
      {/* ROUTES AGENT MOBILE (interface terrain simplifiee)                  */}
      {/* =================================================================== */}
      <Route
        path="/agent"
        element={
          <ProtectedRoute roles={[ROLES.AGENT_DENEIGNEMENT, ROLES.CHEF_DENEIGNEMENT, ROLES.ADMIN]}>
            <MobileHomePage />
          </ProtectedRoute>
        }
      />

      {/* Route mobile pour suivi des zones (hors Layout pour UX mobile) */}
      <Route
        path="/agent/intervention/:id"
        element={
          <ProtectedRoute roles={[ROLES.AGENT_DENEIGNEMENT, ROLES.CHEF_DENEIGNEMENT, ROLES.ADMIN]}>
            <ZonesAgentPageOptimized />
          </ProtectedRoute>
        }
      />

      {/* Route mobile profil agent */}
      <Route
        path="/agent/profil"
        element={
          <ProtectedRoute roles={[ROLES.AGENT_DENEIGNEMENT, ROLES.CHEF_DENEIGNEMENT, ROLES.ADMIN]}>
            <MobileProfilPage />
          </ProtectedRoute>
        }
      />

      {/* Redirection par defaut */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
